import type { OutreachReplyLabel } from 'fym-shared/src/music24/outreach/types';
import { useTRPCAPI, useTRPCClientAPI, useTRPCMutation, useTRPCQuery } from '../../../../trpc/trpc';
import type { LiveLinkReminder, ReplyThread } from '../outreachTypes';
import { useInvalidateOutreach } from './useInvalidateOutreach';
import { useRetry } from './useRetry';

/** One thread of the inbox (frames g1, g2) and its writes. A reply is never sent without a click. */
export function useReplyThread(accountId: number, replyUuid: string | null) {
  const trpcAPI = useTRPCAPI();
  const trpcClient = useTRPCClientAPI();
  const invalidate = useInvalidateOutreach();
  const query = useTRPCQuery(
    trpcAPI.outreach.inbox.get.queryOptions(
      { accountId, replyUuid: replyUuid ?? '' },
      { enabled: replyUuid !== null, retry: false }
    )
  );
  const retry = useRetry(query.refetch);
  const done = () => invalidate(['inbox', 'home']);

  const answer = useTRPCMutation({
    mutationFn: (input: { replyUuid: string; body: string }) =>
      trpcClient.outreach.inbox.answer.mutate({ accountId, ...input }),
    onSuccess: done,
  });
  const setLabel = useTRPCMutation({
    mutationFn: (input: { replyUuid: string; label: OutreachReplyLabel }) =>
      trpcClient.outreach.inbox.setLabel.mutate({ accountId, ...input }),
    onSuccess: done,
  });
  const markHandled = useTRPCMutation({
    mutationFn: (uuid: string) => trpcClient.outreach.inbox.markHandled.mutate({ accountId, replyUuid: uuid }),
    onSuccess: done,
  });
  const remindLiveLink = useTRPCMutation({
    mutationFn: (uuid: string) =>
      trpcClient.outreach.inbox.remindLiveLink.mutate({ accountId, threadId: uuid }) as Promise<LiveLinkReminder>,
    onSuccess: () => invalidate(['inbox']),
  });

  return {
    thread: query.data as ReplyThread | undefined,
    isLoading: query.isLoading,
    error: query.error,
    retry,
    sendAnswer: answer.mutateAsync,
    isSending: answer.isPending,
    sendError: answer.error,
    setLabel: setLabel.mutateAsync,
    markHandled: markHandled.mutateAsync,
    remindLiveLink: remindLiveLink.mutateAsync,
    isReminding: remindLiveLink.isPending,
  };
}
